import React, { useEffect, useState, createElement } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { MarketingHero } from './components/MarketingHero';
import { ProductDemo } from './components/ProductDemo';
import { AICoworkers } from './components/AICoworkers';
import { StayInControl } from './components/StayInControl';
import { ToolComparison } from './components/ToolComparison';
import { HowItWorks } from './components/HowItWorks';
import { HowItWorksConnections } from './components/HowItWorksConnections';
import { AlwaysOn } from './components/AlwaysOn';
import { CapabilityGrid } from './components/CapabilityGrid';
import { QuickCapture } from './components/QuickCapture';
import { LivesInSlack } from './components/LivesInSlack';
import { ConnectedWorkflow } from './components/ConnectedWorkflow';
import { ComputerUse } from './components/ComputerUse';
import { Pricing } from './pages/Pricing';
import { WallOfLove } from './components/WallOfLove';
import { NarrativeSection } from './components/NarrativeSection';
import { MemorySection } from './components/MemorySection';
import { SecuritySection } from './components/SecuritySection';
import { PricingSection } from './components/PricingSection';
import { ClosingCTA } from './components/ClosingCTA';
import { Layout } from './components/Layout';
import { IntroSplash } from './components/IntroSplash';
import { Terms } from './pages/Terms';
import { AffiliateTerms } from './pages/AffiliateTerms';
import { Privacy } from './pages/Privacy';
import { VulnerabilityDisclosure } from './pages/VulnerabilityDisclosure';
import { Contact } from './pages/Contact';
import { Support } from './pages/Support';
import { Careers } from './pages/Careers';
import { Enterprise } from './pages/Enterprise';
import { EnterprisePlatform } from './pages/EnterprisePlatform';
import { ClientManagement } from './pages/ClientManagement';
import { GrowthMarketing } from './pages/GrowthMarketing';
import { FoundersExecs } from './pages/FoundersExecs';
import { Signup } from './pages/Signup';
import { TrialFlow } from './pages/TrialFlow';
import { Blog } from './pages/Blog';
import { Research } from './pages/Research';
import { CaseStudies } from './pages/CaseStudies';
import { Changelog } from './pages/Changelog';
import { Security } from './pages/Security';
import { Integrations } from './pages/Integrations';
import { IntegrationDetail } from './pages/IntegrationDetail';
import { Bespoke } from './pages/Bespoke';
import { Consulting } from './pages/Consulting';
import { CompareChatGPT } from './pages/CompareChatGPT';
import { GigaForChatGPT } from './pages/GigaForChatGPT';
import { GigaForChatGPT2 } from './pages/GigaForChatGPT2';
import { CompanyBrain } from './pages/CompanyBrain';
import { WatchGigaDemo } from './pages/WatchGigaDemo';
import { BookDemo } from './pages/BookDemo';
import { Agents } from './pages/Agents';
import { Home } from './pages/Home';
import { HomeAlternative } from './pages/HomeAlternative';
import { HomeArchitecture } from './pages/HomeArchitecture';
import { EcommerceHome } from './pages/EcommerceHome';
import { FutureHome } from './pages/FutureHome';
import { WorkspaceHome } from './pages/WorkspaceHome';
import { HomeTeamWorkspace } from './pages/HomeTeamWorkspace';
import { ReferralCapture } from './pages/ReferralCapture';
import { Dashboard } from './pages/Dashboard';
import { WorkflowShow } from './pages/WorkflowShow';
import { OnboardingFunnel } from './pages/OnboardingFunnel';
import { WorkspaceAlreadyConnected } from './pages/WorkspaceAlreadyConnected';
import { SphereDirections } from './pages/SphereDirections';
import { ChatTransition } from './pages/ChatTransition';
import { UseCasesConcepts } from './pages/UseCasesConcepts';
import { FontOptions } from './pages/FontOptions';
import { ClaudeFont } from './pages/ClaudeFont';
import { CreatorProgram } from './pages/CreatorProgram';
import { ViktorAlternative } from './pages/ViktorAlternative';
import { FeatureDetail } from './pages/FeatureDetail';
import { AIAgents } from './pages/AIAgents';
import { SignIn } from './pages/SignIn';
import { Developers } from './pages/Developers';
import { NewInfrastructure } from './pages/NewInfrastructure';
import { Alt } from './pages/Alt';
import { ExportDashboards } from './pages/ExportDashboards';
import { CopyModeProvider } from './components/copy/copyMode';
import { resolveRouteMeta, isNoindexRoute } from './lib/routeMeta';
/**
 * Scrolls the window to the top on every route change.
 */
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
/**
 * Sets <title> and <meta name="description"> per route.
 * Gets baked into each static HTML file by the prerenderer.
 */
function PageMeta() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    // Titles/descriptions live in src/lib/routeMeta.ts so the build-time
    // prerender can bake the SAME values into each static HTML file.
    const {
      title,
      desc
    } = resolveRouteMeta(pathname);
    document.title = title;
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalHref = `https://getgiga.com${pathname === '/' ? '/' : pathname}`;
    if (!isNoindexRoute(pathname)) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalHref);
    } else if (canonical) {
      canonical.remove();
    }
    const meta = document.querySelector('meta[name="description"]');
    if (desc) {
      if (meta) {
        meta.setAttribute('content', desc);
      } else {
        const el = document.createElement('meta');
        el.name = 'description';
        el.content = desc;
        document.head.appendChild(el);
      }
    }
    // Keep search engines out of the /unlisted and /app-screen-mockups
    // namespaces. This mirrors the noindex meta baked into the prerendered
    // HTML (see prerender.mjs) so it's correct on client-side navigation too.
    let robots = document.querySelector('meta[name="robots"]');
    if (isNoindexRoute(pathname)) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else if (robots) {
      // Returning to a public route — remove the restrictive directive.
      robots.remove();
    }
  }, [pathname]);
  return null;
}
export function App() {
  return <CopyModeProvider data-id="element-0" data-mp="2phya7lencf6v">
      <ScrollToTop data-id="element-1" data-mp="2pjivekf2mls2" />
      <PageMeta data-id="element-2" data-mp="33go5cvhwo4sl" />
      {/* Global Prado-style film grain over the entire app */}
      <div className="grain-global" aria-hidden="true" data-id="element-3" data-mp="2yhutt8m6inv2" />
      <Routes data-id="element-4" data-mp="2yjea2z7u4ywu">
        {/* ──────────────────────────────────────────────────────────────
         APP SCREEN MOCKUPS — product/app UI mockups the marketing team uses
         for reviews. These are NOT the live product (the real signup/login
         lives on app.getgiga.com). Grouped under /app-screen-mockups and
         marked noindex (see routeMeta.isNoindexRoute) so search engines skip
         them. Not linked from the site nav.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/app-screen-mockups/signup" element={<Signup data-id="element-6" data-mp="344ddvj169qzn" />} data-id="element-5" data-mp="2zbu0on6ecxvd" />
        {/* Direct routes onto individual signup screens, so they can be opened
         from the route dropdown without clicking through the flow. */}
        <Route path="/app-screen-mockups/google-consent" element={<Signup data-id="element-8" data-mp="2uek5eqwbtrzm" />} data-id="element-7" data-mp="2ue20z6zig7gm" />
        <Route path="/app-screen-mockups/google-permissions" element={<Signup data-id="element-10" data-mp="2gfc49sgls9ss" />} data-id="element-9" data-mp="2uby0kz0tlql3" />
        <Route path="/app-screen-mockups/giga-chat" element={<Signup data-id="element-12" data-mp="2kge5ufxzsxxx" />} data-id="element-11" data-mp="2gesu4xx72ppb" />
        <Route path="/app-screen-mockups/sign-in" element={<SignIn data-id="element-14" data-mp="2khp7oa7vnvme" />} data-id="element-13" data-mp="2ketbaqbohf02" />
        <Route path="/app-screen-mockups/trial" element={<TrialFlow data-id="element-16" data-mp="30uvhywa3dqor" />} data-id="element-15" data-mp="30v3w0rhyb21j" />
        <Route path="/app-screen-mockups/dashboard" element={<Dashboard data-id="element-18" data-mp="2vxc2xu5kl9vr" />} data-id="element-17" data-mp="30t139zpeyut4" />
        <Route path="/app-screen-mockups/workflow-show" element={<WorkflowShow data-id="element-20" data-mp="2qwel5ioh2hus" />} data-id="element-19" data-mp="2vx291vhemiks" />
        <Route path="/app-screen-mockups/onboarding-funnel" element={<OnboardingFunnel data-id="element-22" data-mp="2lymhxsidn9ba" />} data-id="element-21" data-mp="2qvwjg6j1jbfh" />
        <Route path="/app-screen-mockups/slack-oauth-callback" element={<WorkspaceAlreadyConnected data-id="element-24" data-mp="2hw9fpe2u9tj9" />} data-id="element-23" data-mp="2lxbddqh3xxj4" />
        {/* Figma export surface for the home page's Workspace screens. */}
        <Route path="/app-screen-mockups/export-dashboards" element={<ExportDashboards data-id="element-26" data-mp="2d3yco2csxrmh" />} data-id="element-25" data-mp="2d5j6fpral6ja" />

        {/* Waitlist referral capture stays PUBLIC + functional: /r/<code>
         records the inbound referral and forwards to app.getgiga.com. */}
        <Route path="/r/:code" element={<ReferralCapture data-id="element-28" data-mp="1y8aeatu5zyqi" />} data-id="element-27" data-mp="235il5y9v4gcg" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (standalone, no marketing chrome) — internal review /
         experimental screens. Grouped under /unlisted and marked noindex.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted/sphere-directions" element={<SphereDirections data-id="element-30" data-mp="dscbxb2oyd0a" />} data-id="element-29" data-mp="dpgcfib73g9q" />
        <Route path="/unlisted/chat-transition" element={<ChatTransition data-id="element-32" data-mp="iqdqbip69o5f" />} data-id="element-31" data-mp="ip2rzyehx8lp" />
        <Route path="/unlisted/use-cases-concepts" element={<UseCasesConcepts data-id="element-34" data-mp="nncihvp9zl0z" />} data-id="element-33" data-mp="nonlvuet6qrk" />
        <Route path="/unlisted/font-options" element={<FontOptions data-id="element-36" data-mp="3m72s6rdt89or" />} data-id="element-35" data-mp="nmba5yzn9cmy" />
        <Route path="/unlisted/claude-font" element={<ClaudeFont data-id="element-38" data-mp="3r54k81t7rjcd" />} data-id="element-37" data-mp="3m7w0hwsf3790" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (rendered inside the marketing Layout) — home variants,
         unlinked offering pages, and pages parked for later. Same /unlisted
         prefix + noindex, but keep the header/footer chrome so they still
         look like real pages during review.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted" element={<Layout data-id="element-40" data-mp="38cjv8vgmxydu" />} data-id="element-39" data-mp="38dbq242ers32">
          {/* Home variants / previews */}
          <Route path="architecture" element={<HomeArchitecture data-id="element-42" data-mp="2f9hlcvmfxjir" />} data-id="element-41" data-mp="2f6lpdcu5l4wy" />
          <Route path="home-alt" element={<HomeAlternative data-id="element-44" data-mp="2k4n6hs80qu5s" />} data-id="element-43" data-mp="2k5f4tat035zr" />
          <Route path="ecommerce" element={<EcommerceHome data-id="element-46" data-mp="2p51hnj4x2k81" />} data-id="element-45" data-mp="2k81cdafw5lhz" />
          <Route path="future" element={<FutureHome data-id="element-48" data-mp="2p3gnhumil3ap" />} data-id="element-47" data-mp="2p5uglxxmw5fm" />
          <Route path="agents" element={<Agents data-id="element-50" data-mp="2u330c2yfkhiz" />} data-id="element-49" data-mp="2u4xm1jhj0do4" />
          <Route path="workspace" element={<WorkspaceHome data-id="element-52" data-mp="1wgnfqsapj3r7" />} data-id="element-51" data-mp="2u3mgpf8fmxuw" />
          <Route path="team-workspace" element={<HomeTeamWorkspace data-id="element-54" data-mp="1wignkax23u0s" />} data-id="element-53" data-mp="1whoup7uugae7" />
          {/* Offering pages, currently unlinked */}
          <Route path="enterprise" element={<Enterprise data-id="element-56" data-mp="26etifc4c8dx4" />} data-id="element-55" data-mp="21g8kxkgh08cd" />
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-58" data-mp="26dihdk2a1k9p" />} data-id="element-57" data-mp="26ejr9l7k40pu" />
          <Route path="bespoke" element={<Bespoke data-id="element-60" data-mp="2b6lbbtayuvqf" />} data-id="element-59" data-mp="2b59wmyfzg3kj" />
          <Route path="consulting" element={<Consulting data-id="element-62" data-mp="1dq5t95ggx4ea" />} data-id="element-61" data-mp="2b737yr1jjbyv" />
          <Route path="security" element={<Security data-id="element-64" data-mp="1dpmlghkj7wdz" />} data-id="element-63" data-mp="1dqxouga2f24k" />
          {/* Content, parked until ready */}
          <Route path="blog" element={<Blog data-id="element-66" data-mp="1ino5p5tp21r1" />} data-id="element-65" data-mp="1iofnndudc0yu" />
          <Route path="research" element={<Research data-id="element-68" data-mp="3vz8tkg6c5wjr" />} data-id="element-67" data-mp="1in4jve0xat7q" />
          <Route path="case-studies" element={<CaseStudies data-id="element-70" data-mp="3vxef9kpkl2on" />} data-id="element-69" data-mp="3vyh6jtqt1753" />
          <Route path="changelog" element={<Changelog data-id="element-72" data-mp="3ryyn72emt44d" />} data-id="element-71" data-mp="3ry6p9kxkauax" />
          {/* Use cases, parked until ready */}
          <Route path="use-cases/founders-execs" element={<FoundersExecs data-id="element-74" data-mp="3mx9alibrgie6" />} data-id="element-73" data-mp="3myavsfmhql9m" />
          <Route path="use-cases/client-management" element={<ClientManagement data-id="element-76" data-mp="3hyxx8xm9bpr7" />} data-id="element-75" data-mp="3hzpv6f3btzkn" />
          <Route path="use-cases/growth-marketing" element={<GrowthMarketing data-id="element-78" data-mp="3d0wdsbkx5of7" />} data-id="element-77" data-mp="3i1tvyo5xiigp" />
          {/* Comparison pages, parked until ready */}
          <Route path="compare/chatgpt" element={<CompareChatGPT data-id="element-80" data-mp="388d4hqt9lfg7" />} data-id="element-79" data-mp="389edlpqpzrva" />
        </Route>

        {/* ──────────────────────────────────────────────────────────────
         PUBLIC marketing site — indexable.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/" element={<Layout data-id="element-82" data-mp="2uaeraoqojp2a" />} data-id="element-81" data-mp="2ua575hsbgc4i">
          {/* The four-pillar platform page is the home page. The previous
           Tasklet-style home now lives at /ai-coworker. */}
          <Route index element={<Alt data-id="element-84" data-mp="2fnpq8igs53ts" />} data-id="element-83" data-mp="2pdejo9e6me92" />
          <Route path="ai-coworker" element={<Home data-id="element-86" data-mp="2fm4wgv2ahowz" />} data-id="element-85" data-mp="2fn6itvor9xu0" />
          {/* The previous home page, kept reachable now that the ported page
           owns "/". Same component as /ai-coworker. */}
          <Route path="home-old" element={<Home data-id="element-88" data-mp="26ocfznku0b80" />} data-id="element-87" data-mp="26njild7riy2j" />
          <Route path="terms" element={<Terms data-id="element-90" data-mp="2ao3dea4v5pli" />} data-id="element-89" data-mp="26ounxhguwdvr" />
          <Route path="affiliate-terms-reditus" element={<AffiliateTerms data-id="element-92" data-mp="2aqfvcnamfzyk" />} data-id="element-91" data-mp="2anlcv1b64p7s" />
          <Route path="privacy" element={<Privacy data-id="element-94" data-mp="2yez5z9h58ott" />} data-id="element-93" data-mp="2ye7f2byhrf9v" />
          <Route path="vulnerability-disclosure" element={<VulnerabilityDisclosure data-id="element-96" data-mp="33dk493cu4yfm" />} data-id="element-95" data-mp="2yeplg1evb9vi" />
          <Route path="contact" element={<Contact data-id="element-98" data-mp="33ev6gyqmty4m" />} data-id="element-97" data-mp="33cit6yvtkbxw" />
          <Route path="support" element={<Support data-id="element-100" data-mp="2pkufws9u3hfc" />} data-id="element-99" data-mp="2pm5jos3a4p6g" />
          <Route path="developers" element={<Developers data-id="element-102" data-mp="2tmz7zmkmw5zv" />} data-id="element-101" data-mp="2pldqfnx5n3jc" />
          <Route path="developers/api" element={<Developers data-id="element-104" data-mp="2tl4lw4xmm7u7" />} data-id="element-103" data-mp="2tm5yiduaj2e1" />
          <Route path="developers/openapi" element={<Developers data-id="element-106" data-mp="3hc090or50ev4" />} data-id="element-105" data-mp="3hcthbu5qvcfd" />
          <Route path="developers/authentication" element={<Developers data-id="element-108" data-mp="3m41qcp35wvs0" />} data-id="element-107" data-mp="3hap60r5inb52" />
          <Route path="developers/webhooks" element={<Developers data-id="element-110" data-mp="1aojg2lvowst0" />} data-id="element-109" data-mp="3m4biai7rpd0c" />
          <Route path="developers/mcp" element={<Developers data-id="element-112" data-mp="1amyiene2wtqx" />} data-id="element-111" data-mp="1anrjp8u9qr1o" />
          <Route path="new" element={<NewInfrastructure data-id="element-114" data-mp="15pzuiml0dj16" />} data-id="element-113" data-mp="1am6pxlfs3c4v" />
          {/* /alt was this page's home before the swap. It renders the page
           directly rather than redirecting to "/": a Navigate drops the query
           string, so any preview or campaign link carrying params landed on a
           blank frame instead of the page. */}
          <Route path="alt" element={<Alt data-id="element-116" data-mp="rpxel5w9gzok" />} data-id="element-115" data-mp="rqqd5jl2givm" />
          {/* Enterprise positioning variant. Lives at a top-level path so it is
           easy to open and preview directly; kept out of the index via
           NOINDEX_ROUTES. */}
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-118" data-mp="wp1pc0yu35h0" />} data-id="element-117" data-mp="11p65tqzqs078" />
          <Route path="careers" element={<Careers data-id="element-120" data-mp="wquwdhdczrpj" />} data-id="element-119" data-mp="wnyz7v9c5725" />
        <Route path="pricing" element={<Pricing data-id="element-122" data-mp="2d1yuuu15eajl" />} data-id="element-121" data-mp="2d2rsn5i4pppl" />
          <Route path="giga-for-chatgpt" element={<GigaForChatGPT data-id="element-124" data-mp="283x8o08fdv3w" />} data-id="element-123" data-mp="2d1grzejzcy2p" />
          <Route path="chatgpt" element={<GigaForChatGPT2 data-id="element-126" data-mp="283nl0jaus01d" />} data-id="element-125" data-mp="282vnv41lxu8z" />
          <Route path="company-brain" element={<CompanyBrain data-id="element-128" data-mp="hnv3um3w3rcd" />} data-id="element-127" data-mp="hmtgpj9lneea" />
          <Route path="giga-for-chatgpt-2" element={<Navigate to="/chatgpt" replace data-id="element-130" data-mp="mlmr32tlapa8" />} data-id="element-129" data-mp="honzaqxeeuf7" />
          <Route path="creator-program" element={<CreatorProgram data-id="element-132" data-mp="qnhuqdyzter6" />} data-id="element-131" data-mp="mmg24fzl00y6" />
          <Route path="viktor-alternative" element={<ViktorAlternative data-id="element-134" data-mp="qm6siil74f26" />} data-id="element-133" data-mp="qn8bd98ge5ug" />
          <Route path="integrations" element={<Integrations data-id="element-136" data-mp="vndycc3s3467" />} data-id="element-135" data-mp="vmco29ul6lpj" />
          {/* Data-driven per-integration pages, sourced from the Giga catalog
            (single source of truth). Replaces the old one-off pages. */}
          <Route path="integrations/:slug" element={<IntegrationDetail data-id="element-138" data-mp="15ixumzm984vh" />} data-id="element-137" data-mp="15jz2kv7z4b8z" />
          <Route path="features/:slug" element={<FeatureDetail data-id="element-140" data-mp="1acs83lb9nnm4" />} data-id="element-139" data-mp="1abqx1gu9314e" />
          {/* AI Agents has a bespoke page rather than the shared feature
            template. Static segments outrank the dynamic one in the router, so
            this wins over `features/:slug` regardless of order here. */}
          <Route path="features/ai-agents" element={<AIAgents data-id="element-142" data-mp="1oc1oakzh2hq3" />} data-id="element-141" data-mp="1ob0d8gighv8d" />
          {/* ARCHIVE, deliberately unlinked. The template rendering of the same
            feature, kept reachable so nothing is lost while the new page is
            being judged. */}
          <Route path="features/ai-agents/alt" element={<FeatureDetail slugOverride="ai-agents" data-id="element-144" data-mp="1ogr652p6n1p2" />} data-id="element-143" data-mp="1tej1k6p1d5y0" />
          {/* The feature was called Assistants. Existing links must not 404. */}
          <Route path="features/assistants" element={<Navigate to="/features/ai-agents" replace data-id="element-146" data-mp="1khs71yq4tz5j" />} data-id="element-145" data-mp="1kgqt9mhqeyk4" />
          <Route path="features/connect-to-slack" element={<Navigate to="/features/giga-in-slack" replace data-id="element-148" data-mp="2c6li7i1ogm89" />} data-id="element-147" data-mp="2h4dcgipsokfm" />
          <Route path="features/integrate-with-databases" element={<Navigate to="/features/connect-to-databases" replace data-id="element-150" data-mp="27fd78hkp8ssz" />} data-id="element-149" data-mp="2c74zqxnf18lr" />
          <Route path="features/backed-by-a-full-computer" element={<Navigate to="/features/giga-cloud-computer" replace data-id="element-152" data-mp="23diazrhml5m2" />} data-id="element-151" data-mp="27dio98sznarj" />
          <Route path="features/giga-cloud-computer" element={<Navigate to="/features/a-full-computer" replace data-id="element-154" data-mp="2zxh829mkp7cv" />} data-id="element-153" data-mp="2zwpnruyfew1w" />
          <Route path="features/browser-control" element={<Navigate to="/features/cloud-browser" replace data-id="element-156" data-mp="2v3mpxamm90fw" />} data-id="element-155" data-mp="2v4emoory947r" />
          <Route path="demo" element={<WatchGigaDemo data-id="element-158" data-mp="ta0dk2bh9d8n" />} data-id="element-157" data-mp="t98f8jqhx1eo" />
          <Route path="demo/book" element={<BookDemo data-id="element-160" data-mp="y5yz7i5f8h5d" />} data-id="element-159" data-mp="y6qrok3q1yrf" />
        </Route>
      </Routes>
    </CopyModeProvider>;
}